import { useState, useRef, useEffect, useCallback } from 'react';
import { useStore } from 'react-redux';
import useDocumentStorageService, { normalizeStoragePathWithSlash } from './documentStorageService';

const WAITING_TIMEOUT = 100;

type GetArtifactsInfoFn<T> = (ids: string[]) => Promise<T[]>;

type ArtifactPromise = {
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
};

const useDocumentStorageReceiver = <T = unknown>(getDocumentsInfo: GetArtifactsInfoFn<T>) => {
  const { setArtifactsToStore, mapDocStorageDataToArtifactStore } = useDocumentStorageService();
  const reduxStore = useStore();

  const [batchedIds, setBatchedIds] = useState<string[]>([]);
  const [promises, setPromises] = useState<ArtifactPromise[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Batch fetch effect
  useEffect(() => {
    if (!batchedIds.length || !promises.length) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(async () => {
      const uniqueIds = Array.from(new Set(batchedIds));
      try {
        const info = await getDocumentsInfo(uniqueIds);
        promises.forEach(({ resolve }) => resolve(info));
      } catch (error) {
        promises.forEach(({ reject }) => reject(error));
      } finally {
        setBatchedIds([]);
        setPromises([]);
        timeoutRef.current = null;
      }
    }, WAITING_TIMEOUT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchedIds, promises]);

  const getDocumentsData = useCallback(async (paths: string[]): Promise<Record<string, any>[]> => {
    return new Promise((resolve, reject) => {
      setPromises(prev => [...prev, { resolve, reject }]);
      setBatchedIds(prev => [...prev, ...paths]);
    });
  }, []);

  const getDocumentByPaths = useCallback(
    async (paths: string[]): Promise<any[]> => {
      // Get actual artifacts from Redux store
      const state = reduxStore.getState() as any;
      const { artifacts } = state;

      const missing = paths.filter(path => {
        const key = normalizeStoragePathWithSlash(path);
        return !artifacts[key];
      });

      let newArtifacts: Record<string, any> = {};
      if (missing.length) {
        const documents = await getDocumentsData(missing);
        newArtifacts = documents.reduce((acc: Record<string, any>, item: any) => {
          const mapped = mapDocStorageDataToArtifactStore(item);
          acc[item.path] = mapped;
          return acc;
        }, {});
        setArtifactsToStore(newArtifacts);
      }

      // Assemble final result
      return paths
        .map(path => normalizeStoragePathWithSlash(path))
        .map(key => artifacts[key] || newArtifacts[key])
        .filter(Boolean)
        .map(Object.freeze);
    },
    [reduxStore, getDocumentsData, mapDocStorageDataToArtifactStore, setArtifactsToStore],
  );

  return getDocumentByPaths;
};

export default useDocumentStorageReceiver;
